// ─── Resumable (TUS) upload to Supabase Storage ──────────────────────────────
// Supabase Storage exposes a TUS endpoint at
//   https://<ref>.storage.supabase.co/storage/v1/upload/resumable
// that supports RESUMABLE uploads: if the connection drops mid-file, a retry
// continues from the last acknowledged offset instead of restarting. This is a
// big win for big originals (the heavy ~3600px print file) on flaky mobile
// links — exactly where the old single-shot PUT loses the whole file.
//
// Auth: the worker mints a short-lived `authenticated` JWT (the bucket's INSERT
// policy only needs a non-null auth.uid()). We send it as the Authorization
// Bearer token + the anon key as apikey.
//
// SAFE FALLBACK: callers wrap this in try/catch and, on ANY rejection, fall
// back to the existing signed-URL PUT (uploadToSignedUrl). So if TUS is
// unavailable, blocked, or errors, the original upload path still runs.

import * as tus from 'tus-js-client';

export interface TusUploadOptions {
  /** <ref>.storage.supabase.co — from the worker's `tus` block. */
  storageHost: string;
  /** Supabase anon (publishable) key — sent as the `apikey` header. */
  anonKey: string;
  /** Short-lived authenticated JWT (Bearer). */
  token: string;
  bucket: string;
  objectPath: string;
  /** REAL image MIME — never octet-stream (the bucket allowlist 400s it). */
  contentType: string;
  onProgress?: (pct: number) => void;
  /** Optional: mint a fresh JWT when the current one nears expiry mid-upload,
   *  so a long resume doesn't fail on a 401. */
  getFreshToken?: () => Promise<string>;
}

// Supabase requires EXACTLY 6MB chunks for resumable uploads.
const TUS_CHUNK_SIZE = 6 * 1024 * 1024; // 6291456

// ─── Idle watchdog ──────────────────────────────────────────────────────────
// tus-js-client's `retryDelays` ONLY fire on an error/close event — they never
// fire on a socket that hangs OPEN with zero bytes flowing. A hung-open
// connection therefore stalls a background-original upload forever, and with
// `originalSem`=3 three such hangs stall the whole original track. So we add an
// idle-based watchdog (modelled on uploadToSignedUrl.ts's 15s stall watchdog):
// if no upload progress for STALL_MS, ABORT the current `tus.Upload` and start a
// fresh one on the same File — which resumes from the server offset via the
// persisted fingerprint. After WATCHDOG_MAX_RETRIES dead attempts we reject so
// the caller (`uploadOriginal`) lets `finalizeOriginals` retry at submit.
const STALL_MS = 20_000;
const WATCHDOG_MAX_RETRIES = 3;

// ─── Persistent resume storage ───────────────────────────────────────────────
// Without an explicit `urlStorage`, even though tus-js-client v4 defaults to a
// localStorage store when available, a FRESH `tus.Upload` never looks up a prior
// fingerprint — so a reload (or our own abort+restart) couldn't resume. We pass
// an explicit persistent store AND call findPreviousUploads/resumeFromPrevious
// so a same-File restart picks up the server offset. SSR / Private-Mode-Safari /
// sandboxed-iframe (no usable localStorage) falls back to the in-memory noop
// store — `canStoreURLs` already probes for a writable localStorage.
// tus-js-client's `UrlStorage` / `PreviousUpload` interfaces aren't exported and
// `WebStorageUrlStorage` is a runtime-only export (absent from the .d.ts), so we
// describe the minimal structural shape we depend on here.
type TusPreviousUpload = { urlStorageKey?: string; [k: string]: unknown };
interface TusUrlStorage {
  findAllUploads(): Promise<TusPreviousUpload[]>;
  findUploadsByFingerprint(fingerprint: string): Promise<TusPreviousUpload[]>;
  removeUpload(urlStorageKey: string): Promise<void>;
  addUpload(fingerprint: string, upload: TusPreviousUpload): Promise<string>;
}

function makeUrlStorage(): TusUrlStorage {
  try {
    const tusAny = tus as unknown as {
      canStoreURLs?: boolean;
      WebStorageUrlStorage?: new () => TusUrlStorage;
    };
    if (tusAny.canStoreURLs && tusAny.WebStorageUrlStorage) {
      return new tusAny.WebStorageUrlStorage();
    }
  } catch {
    /* fall through to the in-memory store */
  }
  // SSR / Private-Mode-Safari / sandboxed-iframe: no usable localStorage — use a
  // trivial in-memory store (mirrors tus's NoopUrlStorage but keyed so a
  // within-session abort+restart can still find its own fingerprint).
  const mem: Record<string, TusPreviousUpload> = {};
  return {
    findAllUploads: () => Promise.resolve(Object.values(mem)),
    findUploadsByFingerprint: (fp: string) =>
      Promise.resolve(
        Object.values(mem).filter((u) => u.urlStorageKey?.startsWith(`tus::${fp}::`)),
      ),
    removeUpload: (key: string) => {
      delete mem[key];
      return Promise.resolve();
    },
    addUpload: (fp: string, upload: TusPreviousUpload) => {
      const key = `tus::${fp}::${Math.round(Math.random() * 1e12)}`;
      mem[key] = { ...upload, urlStorageKey: key };
      return Promise.resolve(key);
    },
  };
}

export function uploadViaTus(
  file: File | Blob,
  opts: TusUploadOptions,
): Promise<void> {
  const {
    storageHost,
    anonKey,
    token,
    bucket,
    objectPath,
    contentType,
    onProgress,
    getFreshToken,
  } = opts;

  // Shared across watchdog retries so a restart resumes from the persisted
  // fingerprint instead of re-uploading from byte 0.
  const urlStorage = makeUrlStorage();

  return new Promise<void>((resolve, reject) => {
    let currentToken = token;
    let watchdogRetries = 0;
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    // `attempt` builds + starts one `tus.Upload`, armed with an idle watchdog.
    // On a STALL_MS-of-no-progress hang it aborts and re-attempts (resuming);
    // after WATCHDOG_MAX_RETRIES it rejects.
    const attempt = () => {
      if (settled) return;

      let lastProgressAt = Date.now();
      const pollEvery = Math.min(STALL_MS, 5000);
      let watchdog: ReturnType<typeof setInterval> | null = null;
      let aborted = false;

      const clearWatchdog = () => {
        if (watchdog) {
          clearInterval(watchdog);
          watchdog = null;
        }
      };

      const upload = new tus.Upload(file, {
        endpoint: `https://${storageHost}/storage/v1/upload/resumable`,
        // Resumes from the last offset rather than restarting on a transient drop.
        retryDelays: [0, 1000, 3000, 5000, 10000],
        headers: {
          authorization: `Bearer ${currentToken}`,
          apikey: anonKey,
          'x-upsert': 'true',
        },
        // Supabase's TUS server REQUIRES exactly 6MB chunks.
        chunkSize: TUS_CHUNK_SIZE,
        uploadDataDuringCreation: true,
        removeFingerprintOnSuccess: true,
        // Persistent fingerprint store so a same-File restart/reload resumes.
        // (tus's UrlStorage type isn't exported — our structural shape matches.)
        urlStorage: urlStorage as never,
        metadata: {
          bucketName: bucket,
          objectName: objectPath,
          contentType,
          cacheControl: '3600',
        },
        // Swap in a fresh token before each request if the caller provided a
        // refresher — guards a long resume against mid-flight token expiry.
        onBeforeRequest: getFreshToken
          ? async (req) => {
              try {
                const fresh = await getFreshToken();
                if (fresh) {
                  currentToken = fresh;
                  req.setHeader('authorization', `Bearer ${fresh}`);
                }
              } catch {
                /* keep the current token — it may still be valid */
              }
            }
          : undefined,
        onError: (err) => {
          // A watchdog-triggered abort surfaces here as an error; swallow it —
          // `attempt()` already scheduled the resume. Any other error is real.
          clearWatchdog();
          if (aborted) return;
          finish(() => reject(err));
        },
        onProgress: (sent, total) => {
          lastProgressAt = Date.now();
          if (onProgress && total > 0) {
            onProgress(Math.round((sent / total) * 100));
          }
        },
        onSuccess: () => {
          clearWatchdog();
          finish(() => resolve());
        },
      });

      // Idle watchdog: if no progress for STALL_MS, abort + resume (or reject
      // after the final retry). Resets on every onProgress tick, so a slow-but-
      // alive link never trips as long as bytes keep flowing.
      watchdog = setInterval(() => {
        if (settled) {
          clearWatchdog();
          return;
        }
        if (Date.now() - lastProgressAt < STALL_MS) return;
        clearWatchdog();
        aborted = true;
        try {
          // abort() without `true` keeps the fingerprint so the next attempt
          // resumes from the server offset.
          upload.abort();
        } catch {
          /* ignore */
        }
        if (watchdogRetries >= WATCHDOG_MAX_RETRIES) {
          finish(() =>
            reject(
              Object.assign(new Error('TUS upload stalled'), { code: 'STALL' as const }),
            ),
          );
          return;
        }
        watchdogRetries += 1;
        attempt();
      }, pollEvery);

      // Resume from a persisted fingerprint if one survives (reload or a prior
      // abort), else start fresh. Both paths begin transmitting.
      upload
        .findPreviousUploads()
        .then((prev) => {
          if (settled || aborted) return;
          if (prev.length > 0) upload.resumeFromPreviousUpload(prev[0]);
          upload.start();
        })
        .catch(() => {
          if (settled || aborted) return;
          upload.start();
        });
    };

    attempt();
  });
}
